import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const normalizeMathText = (value, renderBareLatex) => {
  const source = String(value ?? '')
    .replace(/\\\[([\s\S]*?)\\\]/g, (_match, expression) => `$$${expression.trim()}$$`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_match, expression) => `$${expression.trim()}$`);
  if (!renderBareLatex) return source;

  // Uploaded answers can contain bare LaTeX next to Korean explanations.
  // Preserve existing math delimiters, prose, spacing and line breaks.
  return source
    .split(/(\$\$[\s\S]*?\$\$|\$[^$\r\n]+\$)/g)
    .map((part) => {
      if (!part || (part.startsWith('$') && part.endsWith('$'))) return part;
      return part.replace(/(?:\\[A-Za-z]+\{[^{}\r\n]*\}|[^\u1100-\u11ff\u3130-\u318f\uac00-\ud7af$\r\n])+/g, (chunk) => {
        if (!/\\[A-Za-z]+/.test(chunk)) return chunk;
        const leadingSpace = chunk.match(/^\s*/)[0];
        const trailingSpace = chunk.match(/\s*$/)[0];
        return `${leadingSpace}$${chunk.trim()}$${trailingSpace}`;
      });
    })
    .join('');
};

const renderMath = (expression, displayMode, key) => {
  try {
    const html = katex.renderToString(expression.trim(), {
      displayMode,
      throwOnError: false,
      strict: false,
      trust: false,
      output: 'html'
    });
    return (
      <span
        className={displayMode ? 'katex-display-segment' : 'katex-inline-segment'}
        dangerouslySetInnerHTML={{ __html: html }}
        key={key}
      />
    );
  } catch (error) {
    return <span className="katex-source-error" key={key}>{displayMode ? `$$${expression}$$` : `$${expression}$`}</span>;
  }
};

const KatexText = ({ value, className = '', renderBareLatex = false }) => {
  const source = normalizeMathText(value, renderBareLatex);
  const parts = [];
  const expressionPattern = /\$\$([\s\S]*?)\$\$|\$([^$\n]+?)\$/g;
  let cursor = 0;
  let match;

  while ((match = expressionPattern.exec(source)) !== null) {
    if (match.index > cursor) {
      parts.push(<span className="katex-plain-text" key={`text-${cursor}`}>{source.slice(cursor, match.index)}</span>);
    }
    const displayMode = match[1] !== undefined;
    parts.push(renderMath(displayMode ? match[1] : match[2], displayMode, `math-${match.index}`));
    cursor = expressionPattern.lastIndex;
  }

  if (cursor < source.length) {
    parts.push(<span className="katex-plain-text" key={`text-${cursor}`}>{source.slice(cursor)}</span>);
  }

  return <div className={`katex-text ${className}`.trim()}>{parts}</div>;
};

export default KatexText;
